<template>
  <div class="app-content-wrapper">
    <div class="search-wrapper">
      <search-component ref="search"
                        :search-condition="searchCondition"
                        :search-buttons="searchButtonList"
                        :fuzzySearchPlaceholder="fuzzySearchPlaceholder"
                        date-picker-format="yyyy-MM-dd"
                        :validFunction="validSearchParams"
                        :showMerchantSearch="true"
                        router-name="/wallet/settlement/batch">

      </search-component>
    </div>
    <div class="table-wrapper">
      <custom-table :table-set="tableCondition"
                    :service-function="getSettlementBatch"
                    :first-parameters="getInitParams()"
                    :is-first-automatic-visit="false"
                    :showMerchantNo="true"
                    ref="table"
                    router-name="/wallet/settlement/batch">
      </custom-table>
    </div>
    <custom-message ref="customMessage"></custom-message>
  </div>
</template>
<script>

import CustomMessage from '@/components/modal/message-box/custom-message.vue';
import { ShowSuccessMessage, ShowErrorMessage } from '@/components/modal/tips/message-tips';
import CustomTable from '@/components/table/custom-table.vue';
import SearchComponent from '@/components/table/search-component.vue';
import { getCurrentMenu } from '@/layout/base/components/inavbar/utils';
import GlobalData from '@/utils/global/index';
import { getEnantiomericValue, initSelectedData, splicingAmountAndCurrency } from '@/utils/tools/constant';
import { formatDateDisplay, getTodayParams, initToday } from '@/utils/tools/time';
import SettlementBalanceService from '@/views/settlement/api/settlement-balance';
import SettlementBatchService from '@/views/settlement/api/settlement-batch';

export default {
  components: {
    SearchComponent,
    CustomTable,
    CustomMessage,
  },
  data() {
    return {
      getTodayParams,
      ...SettlementBatchService,
      ...SettlementBalanceService,
      currencyList: [],
      productTypeList: [],
      downloading: false,
      fuzzySearchPlaceholder: `${this.$t('message.settlement.batchNo')};${this.$t('message.settlement.disbursementNo')}`,
      searchCondition: [
        {
          key: ['startTime', 'endTime'],
          name: 'message.settlement.time',
          defValue: initToday(),
          type: 'date_time',
          timeFormatFunc: (key, date) => {
            if (!date) {
              return;
            }
            const d = new Date(date);
            if (key === 'startTime') {
              d.setHours(0, 0, 0);
              return formatDateDisplay(d);
            } else {
              d.setHours(23, 59, 59);
              return formatDateDisplay(d);
            }
          },
        },
        {
          key: 'settleCurrency',
          name: 'message.settlement.currency',
          type: 'select',
          defValue: '',
          options: this.currencyList,
        },
        {
          key: 'productType',
          name: 'message.tableCommon.productType',
          type: 'select',
          defValue: '',
        },
        {
          key: 'settleStatus',
          name: 'message.settlement.settlementStatus',
          type: 'select',
          defValue: '',
        },
      ],
      searchButtonList: [
        {
          type: 'download',
          permissionCode: 'wallet:settlement:batch:download',
          func: async (param) => {
            if (!this.downloading) {
              this.downloading = true;
              try {
                await this.downloadSettlementBatch(param);
                ShowSuccessMessage(this.$t('message.common.downloadTask'));
              } catch (err) {
                //
              }
              this.downloading = false;
            }
          },
        },
      ],
      tableCondition: [
        { // 结算日期
          titleBar: { type: 'text', title: 'message.settlement.time' },
          key: 'settleDate',
          type: 'text',
        },
        { // 结算批次号
          key: 'batchNo',
          titleBar: { title: 'message.settlement.batchNo', type: 'text' },
          type: 'click',
          subTableDisplays: [
            {
              type: 'link',
              key: 'batchNo',
              router: '/wallet/settlement/details',
              isInternationalization: true,
              valueKey: ['batchNo'],
              paramsKey: ['keywords'],
            },
          ],
        },
        {
          key: 'payoutId',
          titleBar: { title: 'message.settlement.disbursementNo', type: 'text' },
          type: 'text',
        },
        { // 结算币种
          titleBar: { type: 'text', title: 'message.settlement.currency' },
          key: 'settleCurrency',
          type: 'text',
        },
        { // 产品类型
          titleBar: { type: 'text', title: 'message.tableCommon.productType' },
          key: 'productType',
          type: 'text',
          textFunc: (data) => getEnantiomericValue(this.productTypeList, data.productType),
        },
        { // 交易金额
          titleBar: { type: 'text', title: 'message.tableCommon.txnAmount' },
          key: 'saleSuccAmt',
          type: 'text',
        },
        { // 退款金额
          titleBar: { type: 'text', title: 'message.tableCommon.refundAmount' },
          key: 'revokeSuccAmt',
          type: 'text',
        },
        { // 交易处理费
          titleBar: { type: 'text', title: 'message.settlement.txnProcessingAmount' },
          key: 'saleHandleAmt',
          type: 'text',
          itemWidth: '210px',
        },
        { // 退款处理费
          titleBar: { type: 'text', title: 'message.settlement.refundProcessingAmount' },
          key: 'revokeSuccHandleAmt',
          type: 'text',
        },
        { // 交易手续费
          titleBar: { type: 'text', title: 'message.settlement.txnFeeAmount' },
          key: 'saleSuccFeeAmt',
          type: 'text',
        },
        { // 保证金
          titleBar: { type: 'text', title: 'message.settlement.deposit' },
          key: 'depositAmt',
          type: 'text',
        },
        { // 释放保证金
          titleBar: { type: 'text', title: 'message.settlement.depositReleaseAmt' },
          key: 'depositReleaseAmt',
          type: 'text',
        },
        { // 拒付金额
          titleBar: { type: 'text', title: 'message.settlement.refuseAmount' },
          key: 'refusalAmount',
          type: 'text',
          textFunc: (data) => data.refusalAmount,
        },
        { // 拒付处理金额
          titleBar: { type: 'text', title: 'message.settlement.refuseProcessingAmount' },
          key: 'refusalHandleAmount',
          type: 'text',
          textFunc: (data) => data.refusalHandleAmount,
          itemWidth: '210px',
        },
        { // Scheme Fee
          titleBar: { type: 'text', title: 'message.tableCommon.schemeAmt' },
          key: 'schemeAmt',
          type: 'text',
          // textFunc: (data) => splicingAmountAndCurrency(data.schemeAmt && data.schemeAmt.currency, data.schemeAmt && data.schemeAmt.amount),
        },
        { // Interchange Fee
          titleBar: { type: 'text', title: 'message.tableCommon.interchangeAmt' },
          key: 'interchangeAmt',
          type: 'text',
          // textFunc: (data) => splicingAmountAndCurrency(data.interchangeAmt && data.interchangeAmt.currency, data.interchangeAmt && data.interchangeAmt.amount),
        },
        { // Markup
          titleBar: { type: 'text', title: 'message.tableCommon.markupAmt' },
          key: 'markupAmt',
          type: 'text',
          // textFunc: (data) => splicingAmountAndCurrency(data.markupAmt && data.markupAmt.currency, data.markupAmt && data.markupAmt.amount),
        },
        // { // 税费
        //   titleBar: { type: 'text', title: 'message.tableCommon.taxes' },
        //   key: 'taxes',
        //   type: 'text',
        //   // textFunc: (data) => splicingAmountAndCurrency(data.taxes && data.taxes.currency, data.taxes && data.taxes.amount),
        // },
        { // 平台费
          titleBar: { type: 'text', title: 'message.tableCommon.platformFee' },
          key: 'platformFeeAmt',
          type: 'text',
        },
        { // 欺诈管理费
          titleBar: { type: 'text', title: 'message.tableCommon.fraudAdministrationFee' },
          key: 'fraudAdministrationFee',
          type: 'text',
        },
        { // 风控管理费
          titleBar: { type: 'text', title: 'message.settlement.decisionManagementFee' },
          key: 'decisionManagementFeeAmount',
          type: 'text',
        },
        { // 结算金额
          titleBar: { type: 'text', title: 'message.settlement.settlementAmount' },
          key: 'settleAmount',
          type: 'text',
        },
        { // 结算状态
          titleBar: { type: 'text', title: 'message.settlement.settlementStatus' },
          key: 'settleStatusName',
          type: 'text',
        },
        { // 打款时间
          titleBar: { type: 'text', title: 'message.settlement.disbursementTime' },
          key: 'disbursementTime',
          type: 'text',
        },
      ],
    };
  },
  beforeCreate() {
    try {
      const menu = getCurrentMenu(this.$store.getters.permission.menus, {
        path: '/wallet/settlement',
        meta: {
          resourceCode: 'wallet:settlement',
        },
      });
      const index = menu.childrenNode[0].menuUrl;
      if (index !== '/wallet/settlement/batch') {
        this.$router.push({ path: index });
      }
    } catch (err) {
      //
    }
  },
  methods: {
    /**
     * 刷新当前表单
     */
    onRefreshTable() {
      this.$refs.table.refreshTable();
    },
    getInitParams() {
      const payoutId = this.$route.query.payoutId;
      if (payoutId) {
        return {};
      }
      const params = getTodayParams('startTime', 'endTime');
      return params;
    },
    validSearchParams(params) {
      if (params.keywords) {
        return true;
      }
      if (!params.startTime && !params.endTime) {
        ShowErrorMessage(this.$t('message.settlement.searchTimeTip'));
        return false;
      }
      if (params.startTime || params.endTime) {
        if (!params.startTime) {
          ShowErrorMessage(this.$t('message.searchCommon.startTimeTip', { time: this.$t('message.settlement.time') }));
          return false;
        }
        if (!params.endTime) {
          ShowErrorMessage(this.$t('message.searchCommon.endTimeTip', { time: this.$t('message.settlement.time') }));
          return false;
        }
      }

      return true;
    },
    async initMethod() {
      await GlobalData.getDictList(['PRODUCT_TYPE', 'SETTLE_STATUS_PAYOUT']).then((res) => {
        this.productTypeList = initSelectedData(res.PRODUCT_TYPE);
        this.$refs.search.setSearchSelectOptions('productType', this.productTypeList);
        this.$refs.search.setSearchSelectOptions('settleStatus', initSelectedData(res.SETTLE_STATUS_PAYOUT));
      }).catch(() => {
      });

      this.$refs.table.firstVisitHttp();
    },
  },
  mounted() {
    this.initMethod();
    this.getAccountBalances().then(res => {
      this.currencyList = initSelectedData(res, 'currency', 'currency');
      this.$refs.search.setSearchSelectOptions('settleCurrency', this.currencyList);
    });
  },
};
</script>
<style  lang="scss">
#refundDetail .is-bordered-label{
  width: 50%;
}
</style>
